import { DateTime } from 'luxon';
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { StreakApi } from '../API/StreakApi';
import { useAuth } from '../Auth/AuthProvider';
import StreakModal from '../Rewards/StreakModal';
import { Streak } from '../Types/Streak';
import { UserType } from '../Types/User';
import { UserLootBox } from '../Types/UserLootBox';
import { useModals } from './ModalProvider';

const STREAK_HISTORY_WINDOW_DAYS = 7;

interface StreakContextType {
  streak: Streak | null;
  updateStreak: () => Promise<{ streak: Streak; lootBox?: UserLootBox }>;
  showStreakModal: (
    title: string,
    description?: string,
    lootBox?: UserLootBox,
  ) => void;
}

const StreakContext = createContext<StreakContextType | undefined>(undefined);

interface StreakProviderProps {
  children: ReactNode;
}

export const StreakProvider: React.FC<StreakProviderProps> = ({ children }) => {
  const { userId, user, userAuthorizationInfo } = useAuth();
  const { showModal } = useModals();
  let [streakHistory, setStreakHistory] = useState<Streak[]>();
  const [hasShownWelcomeStreakModal, setHasShownWelcomeStreakModal] =
    useState(false);

  const latestStreak = useMemo(() => {
    if (streakHistory === undefined) return undefined;

    return streakHistory?.[0] ?? null;
  }, [streakHistory]);

  const currentStreak = useMemo(() => {
    if (latestStreak?.currentStreakLength === 0) {
      return null;
    }

    return latestStreak;
  }, [latestStreak]);

  const isCurrentStreakExtensionDue = useMemo(() => {
    if (!currentStreak) return null;

    const streakLastExtendedDateTime = DateTime.fromJSDate(
      currentStreak.lastExtendedAt,
    ).setZone(window.env.REACT_APP_DEFAULT_TIMEZONE!);

    const nowDateTime = DateTime.now().setZone(
      window.env.REACT_APP_DEFAULT_TIMEZONE!,
    );

    return !streakLastExtendedDateTime.hasSame(nowDateTime, 'day');
  }, [currentStreak]);

  useEffect(() => {
    if (!userId) return;

    const fetchStreak = async () => {
      try {
        const streakApi = new StreakApi();
        const fetchedStreakHistory = await streakApi.getUserStreakHistory(
          DateTime.now().minus({ days: STREAK_HISTORY_WINDOW_DAYS }).toJSDate(),
        );

        setStreakHistory(fetchedStreakHistory);
      } catch (error) {
        console.error('Error fetching user streak:', error);
      }
    };
    fetchStreak();
  }, [userId]);

  useEffect(() => {
    if (user?.type !== UserType.Student) return;
    if (!userAuthorizationInfo?.canAccessStudentUI) return;
    if (currentStreak === undefined) return;
    if (hasShownWelcomeStreakModal) return;

    if (!currentStreak) {
      showStreakModal(
        '<b>Start your streak</b>',
        "Read stories and earn coins to start your streak. The more you read, the more rewards you'll unlock. Don't stop now – exciting adventures and bonuses await!",
      );
    } else {
      if (isCurrentStreakExtensionDue) {
        showStreakModal(
          `<b>Keep your <b>${currentStreak.currentStreakLength}</b>-Day streak alive!</b>`,
          'Your dedication is paying off. Keep reading and discovering new stories every day to continue your streak. Remember, every day you read, you get closer to amazing rewards and new adventures!',
        );
      }
    }

    setHasShownWelcomeStreakModal(true);
  }, [
    user,
    userAuthorizationInfo?.canAccessStudentUI,
    currentStreak,
    isCurrentStreakExtensionDue,
    hasShownWelcomeStreakModal,
  ]);

  const updateStreak = async () => {
    const streakApi = new StreakApi();
    const { streak: newLatestStreak, lootBox } =
      await streakApi.updateUserStreak();

    // We update the streakHistory variable explicitly to make sure that StreakModal has the latest data right away
    setStreakHistory(
      (streakHistory = [
        newLatestStreak,
        ...(streakHistory ?? []).filter(
          (streak) => streak.id !== newLatestStreak.id,
        ),
      ]),
    );

    const oldLatestStreakDate =
      latestStreak &&
      DateTime.fromJSDate(latestStreak.lastExtendedAt)
        .setZone(window.env.REACT_APP_DEFAULT_TIMEZONE!)
        .startOf('day');

    const newLatestStreakDate = DateTime.fromJSDate(
      newLatestStreak.lastExtendedAt,
    )
      .setZone(window.env.REACT_APP_DEFAULT_TIMEZONE!)
      .startOf('day');

    if (
      !oldLatestStreakDate ||
      !oldLatestStreakDate.hasSame(newLatestStreakDate, 'day')
    ) {
      const title =
        newLatestStreak.currentStreakLength === 1
          ? `<b>Great job! You've started a new streak!</b>`
          : `<b>Great job! You're now on a ${newLatestStreak.currentStreakLength}-Day Streak!</b>`;

      const description =
        'Your dedication is paying off. Keep reading and discovering new stories every day to continue your streak. Remember, every day you read, you get closer to amazing rewards and new adventures!';

      showStreakModal(title, description, lootBox);
    }

    return { streak: newLatestStreak, lootBox };
  };

  const showStreakModal = (
    title: string,
    description?: string,
    lootBox?: UserLootBox,
  ) => {
    description ??= `Read stories and earn coins to ${!currentStreak?.currentStreakLength ? 'start' : 'build'} your streak. The more you read, the more rewards you'll unlock. Don't stop now – exciting adventures and bonuses await!`;

    showModal({
      children: (
        <StreakModal
          streaks={streakHistory}
          title={title}
          description={description}
          lootBox={lootBox}
          onClose={() => {}}
        />
      ),
      onClose: () => {},
      maxWidthClass: 'max-w-[500px] max-md:max-w-[400px] max-sm:max-w-[350px]',
    });
  };

  return (
    <StreakContext.Provider
      value={{
        streak: currentStreak,
        updateStreak,
        showStreakModal,
      }}
    >
      {children}
    </StreakContext.Provider>
  );
};

export const useStreak = (): StreakContextType => {
  const context = useContext(StreakContext);
  if (context === undefined) {
    throw new Error('useStreak must be used within a StreakProvider');
  }
  return context;
};
